import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  TextField,
  Button,
  Grid,
  Alert,
  CircularProgress,
  Divider,
  Tabs,
  Tab,
  FormControlLabel,
  Switch,
  InputAdornment,
  Avatar,
  Snackbar,
  IconButton,
  Tooltip,
  Chip
} from '@mui/material';
import {
  Email,
  WhatsApp,
  Save,
  Science,
  CheckCircle,
  Error as ErrorIcon,
  Visibility,
  VisibilityOff,
  Phone,
  Security,
  Settings,
  Info,
  Refresh,
  Delete
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import {
  getEmailConfiguration,
  getWhatsAppConfiguration,
  updateEmailConfiguration,
  updateWhatsAppConfiguration,
  testEmailConfiguration,
  testWhatsAppConfiguration
} from '../../redux/actions/communicationActions';
import api from '../../services/api';

const CommunicationConfig = () => {
  const dispatch = useDispatch();
  const { loading, error } = useSelector((state) => state.communication || {});
  const { emailConfig, whatsappConfig } = useSelector((state) => state.communication || {});
  
  const [activeTab, setActiveTab] = useState(0);
  const [emailConfigData, setEmailConfigData] = useState({
    smtp_host: '',
    smtp_port: 587,
    smtp_secure: true,
    smtp_username: '',
    smtp_password: '',
    from_email: '',
    from_name: ''
  });
  const [whatsappConfigData, setWhatsappConfigData] = useState({
    business_account_id: '',
    access_token: '',
    phone_number_id: '',
    from_phone_number: '',
    webhook_verify_token: '',
    mode: 'api',
    session_path: ''
  });
  const [whatsappMode, setWhatsappMode] = useState('api'); // 'api' or 'offline'
  const [offlineStatus, setOfflineStatus] = useState({ status: 'disconnected', isReady: false, qrCode: null });
  const [checkingStatus, setCheckingStatus] = useState(false);
  const [showEmailPassword, setShowEmailPassword] = useState(false);
  const [showWhatsAppToken, setShowWhatsAppToken] = useState(false);
  const [testing, setTesting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  useEffect(() => {
    loadConfigurations();
  }, [dispatch]);

  // Poll status when offline mode is active and not ready
  useEffect(() => {
    if (whatsappMode === 'offline' && offlineStatus.status !== 'ready' && offlineStatus.status !== 'error') {
      const interval = setInterval(() => {
        checkOfflineStatus();
      }, 5000); // Poll every 5 seconds
      
      return () => clearInterval(interval);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whatsappMode, offlineStatus.status]);

  const loadConfigurations = async () => {
    const emailResult = await dispatch(getEmailConfiguration());
    if (emailResult.success && emailResult.data) {
      setEmailConfigData({
        smtp_host: emailResult.data.smtp_host || '',
        smtp_port: emailResult.data.smtp_port || 587,
        smtp_secure: emailResult.data.smtp_secure !== false,
        smtp_username: emailResult.data.smtp_username || '',
        smtp_password: emailResult.data.smtp_password || '',
        from_email: emailResult.data.from_email || '',
        from_name: emailResult.data.from_name || ''
      });
    }

    const whatsappResult = await dispatch(getWhatsAppConfiguration());
    if (whatsappResult.success && whatsappResult.data) {
      const mode = whatsappResult.data.mode || 'api';
      setWhatsappMode(mode);
      setWhatsappConfigData({
        business_account_id: whatsappResult.data.business_account_id || '',
        access_token: whatsappResult.data.access_token || '',
        phone_number_id: whatsappResult.data.phone_number_id || '',
        from_phone_number: whatsappResult.data.from_phone_number || '',
        webhook_verify_token: whatsappResult.data.webhook_verify_token || '',
        mode: mode,
        session_path: whatsappResult.data.session_path || ''
      });
      
      // Check offline status if mode is offline
      if (mode === 'offline') {
        checkOfflineStatus();
      }
    }
  };

  const checkOfflineStatus = async () => {
    setCheckingStatus(true);
    try {
      const response = await api.get('/communication/whatsapp-offline/status');
      if (response.data.success) {
        setOfflineStatus(response.data.data);
      }
    } catch (error) {
      console.error('Error checking offline status:', error);
    } finally {
      setCheckingStatus(false);
    }
  };

  const initializeOffline = async () => {
    setCheckingStatus(true);
    try {
      const response = await api.post('/communication/whatsapp-offline/initialize', {
        sessionPath: whatsappConfigData.session_path || null
      });
      if (response.data.success) {
        setSnackbar({ open: true, message: 'WhatsApp offline service initialized. Please scan QR code.', severity: 'success' });
        // Poll for QR code and status
        setTimeout(() => checkOfflineStatus(), 2000);
        const interval = setInterval(async () => {
          await checkOfflineStatus();
          // Check current status after update
          const currentResponse = await api.get('/communication/whatsapp-offline/status');
          if (currentResponse.data.success) {
            const currentStatus = currentResponse.data.data?.status;
            if (currentStatus === 'ready' || currentStatus === 'error') {
              clearInterval(interval);
            }
          }
        }, 3000);
      } else {
        setSnackbar({ open: true, message: response.data.error || 'Failed to initialize', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to initialize offline WhatsApp', severity: 'error' });
    } finally {
      setCheckingStatus(false);
    }
  };

  const clearCacheAndReconnect = async () => {
    if (!window.confirm('Are you sure you want to clear the WhatsApp cache and reconnect? This will disconnect the current session and require you to scan the QR code again.')) {
      return;
    }

    setCheckingStatus(true);
    try {
      const response = await api.post('/communication/whatsapp-offline/clear-cache');
      if (response.data.success) {
        setSnackbar({ open: true, message: 'Cache cleared successfully. Reconnecting...', severity: 'success' });
        // Reset status
        setOfflineStatus({ status: 'disconnected', isReady: false, qrCode: null });
        // Poll for QR code and status
        setTimeout(() => checkOfflineStatus(), 2000);
        const interval = setInterval(async () => {
          await checkOfflineStatus();
          // Check current status after update
          const currentResponse = await api.get('/communication/whatsapp-offline/status');
          if (currentResponse.data.success) {
            const currentStatus = currentResponse.data.data?.status;
            if (currentStatus === 'ready' || currentStatus === 'error') {
              clearInterval(interval);
            }
          }
        }, 3000);
      } else {
        setSnackbar({ open: true, message: response.data.error || 'Failed to clear cache', severity: 'error' });
      }
    } catch (error) {
      setSnackbar({ open: true, message: error.response?.data?.message || 'Failed to clear cache and reconnect', severity: 'error' });
    } finally {
      setCheckingStatus(false);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleEmailConfigChange = (field, value) => {
    setEmailConfigData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleWhatsAppConfigChange = (field, value) => {
    setWhatsappConfigData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSaveEmailConfig = async () => {
    const result = await dispatch(updateEmailConfiguration(emailConfigData));
    if (result.success) {
      setSnackbar({ open: true, message: 'Email configuration saved successfully!', severity: 'success' });
    } else {
      setSnackbar({ open: true, message: result.message || 'Failed to save email configuration', severity: 'error' });
    }
  };

  const handleSaveWhatsAppConfig = async () => {
    const configToSave = {
      ...whatsappConfigData,
      mode: whatsappMode
    };
    const result = await dispatch(updateWhatsAppConfiguration(configToSave));
    if (result.success) {
      setSnackbar({ open: true, message: 'WhatsApp configuration saved successfully!', severity: 'success' });
      // If offline mode, check status after saving
      if (whatsappMode === 'offline') {
        setTimeout(() => checkOfflineStatus(), 1000);
      }
    } else {
      setSnackbar({ open: true, message: result.message || 'Failed to save WhatsApp configuration', severity: 'error' });
    }
  };

  const handleTestEmailConfig = async () => {
    setTesting(true);
    const result = await dispatch(testEmailConfiguration(emailConfigData));
    setTesting(false);
    if (result.success) {
      setSnackbar({ open: true, message: 'Email configuration test successful!', severity: 'success' });
    } else {
      setSnackbar({ open: true, message: result.message || 'Email configuration test failed', severity: 'error' });
    }
  };

  const handleTestWhatsAppConfig = async () => {
    setTesting(true);
    const result = await dispatch(testWhatsAppConfiguration(whatsappConfigData));
    setTesting(false);
    if (result.success) {
      setSnackbar({ open: true, message: 'WhatsApp configuration test successful!', severity: 'success' });
    } else {
      setSnackbar({ open: true, message: result.message || 'WhatsApp configuration test failed', severity: 'error' });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" fontWeight="bold" gutterBottom>
          Communication Configuration
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Configure email and WhatsApp settings for sending invoices and notifications to customers
        </Typography>
      </Box>

      <Card sx={{ boxShadow: 3, borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          bgcolor: 'background.paper'
        }}>
          <Tabs 
            value={activeTab} 
            onChange={handleTabChange}
            sx={{
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                minHeight: 64,
                '&.Mui-selected': {
                  color: 'primary.main',
                  fontWeight: 'bold'
                }
              }
            }}
          >
            <Tab 
              icon={<Email />} 
              iconPosition="start" 
              label="Email Configuration" 
              sx={{ gap: 1 }}
            />
            <Tab 
              icon={<WhatsApp />} 
              iconPosition="start" 
              label="WhatsApp Configuration" 
              sx={{ gap: 1 }}
            />
          </Tabs>
        </Box>

        <CardContent sx={{ p: 4 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 3 }} icon={<ErrorIcon />}>
              {error}
            </Alert>
          )}

          {activeTab === 0 && (
            <Box>
              {/* SMTP Configuration */}
              <Card variant="outlined" sx={{ mb: 3, p: 3, bgcolor: 'grey.50' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                  <Avatar sx={{ bgcolor: 'info.main', width: 40, height: 40 }}>
                    <Settings sx={{ fontSize: 22 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      SMTP Server Configuration
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Configure your email server settings
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="SMTP Host"
                      value={emailConfigData.smtp_host}
                      onChange={(e) => handleEmailConfigChange('smtp_host', e.target.value)}
                      placeholder="smtp.gmail.com"
                      helperText="Gmail: smtp.gmail.com, Outlook: smtp-mail.outlook.com"
                      variant="outlined"
                      sx={{ bgcolor: 'white' }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="SMTP Port"
                      type="number"
                      value={emailConfigData.smtp_port}
                      onChange={(e) => handleEmailConfigChange('smtp_port', parseInt(e.target.value))}
                      placeholder="587"
                      helperText="Gmail: 587, Outlook: 587"
                      variant="outlined"
                      sx={{ bgcolor: 'white' }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="SMTP Username"
                      value={emailConfigData.smtp_username}
                      onChange={(e) => handleEmailConfigChange('smtp_username', e.target.value)}
                      placeholder="your-email@gmail.com"
                      variant="outlined"
                      sx={{ bgcolor: 'white' }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="SMTP Password"
                      type={showEmailPassword ? 'text' : 'password'}
                      value={emailConfigData.smtp_password}
                      onChange={(e) => handleEmailConfigChange('smtp_password', e.target.value)}
                      placeholder="Your app password"
                      helperText="Use app password for Gmail (not your regular password)"
                      variant="outlined"
                      sx={{ bgcolor: 'white' }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowEmailPassword(!showEmailPassword)}
                              edge="end"
                            >
                              {showEmailPassword ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="From Email"
                      type="email"
                      value={emailConfigData.from_email}
                      onChange={(e) => handleEmailConfigChange('from_email', e.target.value)}
                      placeholder="noreply@bhavanijewellery.com"
                      required
                      variant="outlined"
                      sx={{ bgcolor: 'white' }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="From Name"
                      value={emailConfigData.from_name}
                      onChange={(e) => handleEmailConfigChange('from_name', e.target.value)}
                      placeholder="Bhavani Jewellery"
                      variant="outlined"
                      sx={{ bgcolor: 'white' }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={emailConfigData.smtp_secure}
                          onChange={(e) => handleEmailConfigChange('smtp_secure', e.target.checked)}
                          color="primary"
                        />
                      }
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Security fontSize="small" />
                          <Typography>Use SSL/TLS (Recommended)</Typography>
                        </Box>
                      }
                    />
                  </Grid>
                </Grid>
              </Card>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={testing ? <CircularProgress size={20} /> : <Science />}
                  onClick={handleTestEmailConfig}
                  disabled={loading || testing || !emailConfigData.smtp_host}
                  sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
                >
                  Test Configuration
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSaveEmailConfig}
                  disabled={loading || !emailConfigData.smtp_host || !emailConfigData.from_email}
                  sx={{ 
                    borderRadius: 2, 
                    textTransform: 'none',
                    px: 4,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #5568d3 0%, #653d91 100%)',
                    }
                  }}
                >
                  Save Email Configuration
                </Button>
              </Box>
            </Box>
          )}

          {activeTab === 1 && (
            <Box>
              {/* WhatsApp API Configuration */}
              <Card variant="outlined" sx={{ mb: 3, p: 3, bgcolor: 'grey.50' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                  <Avatar sx={{ bgcolor: 'success.main', width: 40, height: 40 }}>
                    <Settings sx={{ fontSize: 22 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      WhatsApp Business API Configuration
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Configure your WhatsApp Business API settings
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={3}>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Business Account ID"
                      value={whatsappConfigData.business_account_id}
                      onChange={(e) => handleWhatsAppConfigChange('business_account_id', e.target.value)}
                      placeholder="123456789012345"
                      helperText="Your WhatsApp Business Account ID"
                      variant="outlined"
                      sx={{ bgcolor: 'white' }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Phone Number ID"
                      value={whatsappConfigData.phone_number_id}
                      onChange={(e) => handleWhatsAppConfigChange('phone_number_id', e.target.value)}
                      placeholder="123456789012345"
                      helperText="Your WhatsApp Business Phone Number ID"
                      variant="outlined"
                      sx={{ bgcolor: 'white' }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="From Phone Number"
                      value={whatsappConfigData.from_phone_number}
                      onChange={(e) => handleWhatsAppConfigChange('from_phone_number', e.target.value)}
                      placeholder="+919876543210"
                      required
                      variant="outlined"
                      helperText="Include country code (e.g., +91 for India)"
                      sx={{ bgcolor: 'white' }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Phone color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Webhook Verify Token"
                      value={whatsappConfigData.webhook_verify_token}
                      onChange={(e) => handleWhatsAppConfigChange('webhook_verify_token', e.target.value)}
                      placeholder="bhavani_jewellery_webhook_2024"
                      helperText="Enter a secure random string. Use the same token in WhatsApp dashboard."
                      variant="outlined"
                      sx={{ bgcolor: 'white' }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Security color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Access Token"
                      type={showWhatsAppToken ? 'text' : 'password'}
                      value={whatsappConfigData.access_token}
                      onChange={(e) => handleWhatsAppConfigChange('access_token', e.target.value)}
                      placeholder="Your WhatsApp Business API access token"
                      helperText="Keep this secure and never share it publicly"
                      variant="outlined"
                      sx={{ bgcolor: 'white' }}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowWhatsAppToken(!showWhatsAppToken)}
                              edge="end"
                            >
                              {showWhatsAppToken ? <VisibilityOff /> : <Visibility />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                </Grid>
              </Card>

              {/* Webhook Configuration Details */}
              <Card variant="outlined" sx={{ mb: 3, p: 3, bgcolor: 'info.light', border: '1px solid', borderColor: 'info.main' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                  <Avatar sx={{ bgcolor: 'info.main', width: 40, height: 40 }}>
                    <WhatsApp sx={{ fontSize: 22 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold" color="info.dark">
                      Webhook Configuration Details
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Use these details to configure webhook in WhatsApp Business API Dashboard
                    </Typography>
                  </Box>
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Box sx={{ bgcolor: 'white', p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block', fontWeight: 'bold', mb: 1 }}>
                        Webhook URL:
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontFamily: 'monospace', 
                          bgcolor: 'grey.100', 
                          p: 1.5, 
                          borderRadius: 1,
                          wordBreak: 'break-all',
                          border: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        {(() => {
                          try {
                            const baseURL = api.defaults?.baseURL || 'http://localhost:5000/api';
                            const webhookPath = '/communication/whatsapp-webhook';
                            // Extract the base domain from baseURL (remove /api if present)
                            const cleanBaseURL = baseURL.replace(/\/api\/?$/, '');
                            const urlObj = new URL(cleanBaseURL);
                            return `${urlObj.protocol}//${urlObj.host}/api${webhookPath}`;
                          } catch (error) {
                            // Fallback if URL parsing fails
                            return 'https://your-domain.com/api/communication/whatsapp-webhook';
                          }
                        })()}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        ⚠️ Replace with your actual production domain (must use HTTPS in production)
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ bgcolor: 'white', p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="caption" color="text.secondary" gutterBottom sx={{ display: 'block', fontWeight: 'bold', mb: 1 }}>
                        Verify Token:
                      </Typography>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontFamily: 'monospace', 
                          bgcolor: whatsappConfigData.webhook_verify_token ? 'success.light' : 'warning.light', 
                          p: 1.5, 
                          borderRadius: 1,
                          wordBreak: 'break-all',
                          border: '1px solid',
                          borderColor: whatsappConfigData.webhook_verify_token ? 'success.main' : 'warning.main',
                          color: whatsappConfigData.webhook_verify_token ? 'success.dark' : 'warning.dark',
                          fontWeight: 'bold'
                        }}
                      >
                        {whatsappConfigData.webhook_verify_token || 'Please enter verify token above'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        ✓ This token must match exactly in WhatsApp Business API dashboard
                      </Typography>
                    </Box>
                  </Grid>
                </Grid>

                <Divider sx={{ my: 3 }} />

                <Box>
                  <Typography variant="body2" fontWeight="bold" gutterBottom color="info.dark">
                    📋 Steps to Configure Webhook in WhatsApp Dashboard:
                  </Typography>
                  <Box component="ol" sx={{ pl: 3, mt: 1, mb: 0 }}>
                    <Typography variant="body2" component="li" sx={{ mb: 1 }}>
                      Go to your Meta Business Suite or WhatsApp Cloud API dashboard
                    </Typography>
                    <Typography variant="body2" component="li" sx={{ mb: 1 }}>
                      Navigate to <strong>Webhooks</strong> section
                    </Typography>
                    <Typography variant="body2" component="li" sx={{ mb: 1 }}>
                      Click <strong>"Configure Webhook"</strong> or <strong>"Edit"</strong>
                    </Typography>
                    <Typography variant="body2" component="li" sx={{ mb: 1 }}>
                      Enter the <strong>Webhook URL</strong> shown above
                    </Typography>
                    <Typography variant="body2" component="li" sx={{ mb: 1 }}>
                      Enter the <strong>Verify Token</strong> shown above (must match exactly)
                    </Typography>
                    <Typography variant="body2" component="li" sx={{ mb: 1 }}>
                      Subscribe to <strong>"messages"</strong> event
                    </Typography>
                    <Typography variant="body2" component="li">
                      Click <strong>"Verify and Save"</strong>
                    </Typography>
                  </Box>
                  <Alert severity="warning" sx={{ mt: 2 }}>
                    <Typography variant="body2">
                      <strong>Important:</strong> The webhook URL must be publicly accessible. For local development, use tools like ngrok or similar tunneling services to expose your local server.
                    </Typography>
                  </Alert>
                </Box>
              </Card>

              <Alert severity="info" icon={<Info />} sx={{ mb: 3 }}>
                <Typography variant="body2" fontWeight="bold" gutterBottom>
                  Setup Instructions:
                </Typography>
                <Typography variant="body2" component="div">
                  1. Create a WhatsApp Business Account
                  <br />
                  2. Set up a WhatsApp Business API app
                  <br />
                  3. Get your Business Account ID and Phone Number ID
                  <br />
                  4. Generate an access token
                  <br />
                  5. Configure webhook using the details shown above
                </Typography>
              </Alert>

              <Divider sx={{ my: 3 }} />

              {/* Mode Selector and Offline Configuration */}
              <Card variant="outlined" sx={{ mb: 3, p: 3, bgcolor: 'grey.50', border: '1px solid', borderColor: 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                  <Avatar sx={{ bgcolor: 'success.main', width: 40, height: 40 }}>
                    <WhatsApp sx={{ fontSize: 22 }} />
                  </Avatar>
                  <Box>
                    <Typography variant="subtitle1" fontWeight="bold">
                      📱 Offline WhatsApp Mode (For Local Systems)
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Use WhatsApp Web - No API credentials needed. Perfect for local installations!
                    </Typography>
                  </Box>
                </Box>

                <FormControlLabel
                  control={
                    <Switch
                      checked={whatsappMode === 'offline'}
                      onChange={(e) => {
                        const newMode = e.target.checked ? 'offline' : 'api';
                        setWhatsappMode(newMode);
                        setWhatsappConfigData(prev => ({ ...prev, mode: newMode }));
                      }}
                      color="success"
                    />
                  }
                  label={
                    <Typography variant="body1" fontWeight="bold">
                      {whatsappMode === 'offline' ? '✓ Offline Mode Active' : 'Switch to Offline Mode'}
                    </Typography>
                  }
                />

                {whatsappMode === 'offline' && (
                  <Box sx={{ mt: 3 }}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Session Path (Optional)"
                          value={whatsappConfigData.session_path}
                          onChange={(e) => handleWhatsAppConfigChange('session_path', e.target.value)}
                          placeholder="Leave empty for default path"
                          helperText="Path to store WhatsApp session. Default: .wwebjs_auth in backend folder"
                          variant="outlined"
                          sx={{ bgcolor: 'white' }}
                        />
                      </Grid>
                    </Grid>

                    {/* Status Display */}
                    <Box sx={{ mt: 3, p: 2, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        Connection Status:
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                        <Chip 
                          label={offlineStatus.status === 'ready' ? 'Connected ✓' : 
                                 offlineStatus.status === 'qr' ? 'Waiting for QR Scan' :
                                 offlineStatus.status === 'connecting' ? 'Connecting...' :
                                 offlineStatus.status === 'error' ? 'Error' : 'Disconnected'}
                          color={offlineStatus.status === 'ready' ? 'success' : 
                                 offlineStatus.status === 'qr' ? 'warning' :
                                 offlineStatus.status === 'error' ? 'error' : 'default'}
                          icon={checkingStatus ? <CircularProgress size={16} /> : null}
                        />
                        {offlineStatus.isReady && (
                          <Chip label="Ready to Send Messages" color="success" variant="outlined" />
                        )}
                      </Box>
                    </Box>

                    {/* QR Code Display */}
                    {offlineStatus.qrCode && (
                      <Box sx={{ mt: 3, p: 3, bgcolor: 'white', borderRadius: 1, border: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
                        <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                          📱 Scan QR Code with WhatsApp:
                        </Typography>
                        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'center' }}>
                          <img 
                            src={offlineStatus.qrCode} 
                            alt="WhatsApp QR Code" 
                            style={{ maxWidth: '300px', border: '2px solid #ddd', borderRadius: '8px' }}
                          />
                        </Box>
                        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: 'block' }}>
                          1. Open WhatsApp on your phone
                          <br />
                          2. Go to Settings → Linked Devices
                          <br />
                          3. Tap "Link a Device"
                          <br />
                          4. Scan this QR code
                        </Typography>
                      </Box>
                    )}

                    <Box sx={{ display: 'flex', gap: 2, mt: 3, flexWrap: 'wrap' }}>
                      <Button
                        variant="outlined"
                        onClick={checkOfflineStatus}
                        disabled={checkingStatus}
                        startIcon={checkingStatus ? <CircularProgress size={20} /> : <Info />}
                      >
                        Check Status
                      </Button>
                      <Button
                        variant="contained"
                        onClick={initializeOffline}
                        disabled={checkingStatus}
                        startIcon={checkingStatus ? <CircularProgress size={20} /> : <WhatsApp />}
                        color="success"
                      >
                        Initialize & Get QR Code
                      </Button>
                      <Button
                        variant="outlined"
                        color="warning"
                        onClick={clearCacheAndReconnect}
                        disabled={checkingStatus}
                        startIcon={checkingStatus ? <CircularProgress size={20} /> : <Refresh />}
                        sx={{
                          borderColor: 'warning.main',
                          color: 'warning.main',
                          '&:hover': {
                            borderColor: 'warning.dark',
                            bgcolor: 'warning.light',
                            color: 'warning.dark'
                          }
                        }}
                      >
                        Clear Cache & Reconnect
                      </Button>
                    </Box>

                    {/* Setup Guide */}
                    <Box sx={{ mt: 3, p: 2, bgcolor: 'info.light', borderRadius: 1, border: '1px solid', borderColor: 'info.main' }}>
                      <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                        📋 Quick Setup Guide:
                      </Typography>
                      <Box component="ol" sx={{ pl: 2, mt: 1 }}>
                        <Typography variant="body2" component="li" sx={{ mb: 1 }}>
                          Click "Initialize & Get QR Code" button
                        </Typography>
                        <Typography variant="body2" component="li" sx={{ mb: 1 }}>
                          Scan the QR code with your phone's WhatsApp
                        </Typography>
                        <Typography variant="body2" component="li" sx={{ mb: 1 }}>
                          Wait for "Connected" status
                        </Typography>
                        <Typography variant="body2" component="li">
                          Start sending invoices via WhatsApp!
                        </Typography>
                      </Box>
                      <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          <strong>Note:</strong> Session is saved locally. You won't need to scan QR code every time unless you log out from WhatsApp Web.
                        </Typography>
                      </Alert>
                      <Alert severity="warning" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                          <strong>Troubleshooting:</strong> If you're experiencing connection issues, click "Clear Cache & Reconnect" to clear the .wwebjs_cache folder and reconnect. This will require scanning the QR code again.
                        </Typography>
                      </Alert>
                    </Box>
                  </Box>
                )}
              </Card>

              <Divider sx={{ my: 3 }} />

              <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  startIcon={testing ? <CircularProgress size={20} /> : <Science />}
                  onClick={handleTestWhatsAppConfig}
                  disabled={loading || testing || !whatsappConfigData.business_account_id}
                  sx={{ borderRadius: 2, textTransform: 'none', px: 3 }}
                >
                  Test Configuration
                </Button>
                <Button
                  variant="contained"
                  startIcon={<Save />}
                  onClick={handleSaveWhatsAppConfig}
                  disabled={loading || !whatsappConfigData.business_account_id || !whatsappConfigData.from_phone_number}
                  color="success"
                  sx={{ 
                    borderRadius: 2, 
                    textTransform: 'none',
                    px: 4,
                    background: 'linear-gradient(135deg, #11998e 0%, #38ef7d 100%)',
                    '&:hover': {
                      background: 'linear-gradient(135deg, #0d7a6f 0%, #2dd169 100%)',
                    }
                  }}
                >
                  Save WhatsApp Configuration
                </Button>
              </Box>
            </Box>
          )}
        </CardContent>
      </Card>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
          icon={snackbar.severity === 'success' ? <CheckCircle /> : <ErrorIcon />}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default CommunicationConfig;
